import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import NotificationContext from '../context/NotificationContext';
import {
  Box, Container, Paper, Typography, TextField, Button,
  Snackbar, Alert, CircularProgress, Autocomplete, Chip, Divider,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import GoogleAddressSearch from '../components/GoogleAddressSearch';
import iconUrl from 'leaflet/dist/images/marker-icon.png';
import iconShadowUrl from 'leaflet/dist/images/marker-shadow.png';
import { useTranslation } from 'react-i18next';
import { NAV_BG, AMBER } from '../theme';

const DefaultIcon = L.icon({
  iconUrl,
  shadowUrl: iconShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

function ClickHandler({ onMapClick }) {
  useMapEvents({ click(e) { onMapClick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 16);
  }, [position, map]);
  return null;
}


const AddParentPage = () => {
  const { authTokens, backendUrl, pageTitle, profile } = useContext(AuthContext);
  const { showToast } = useContext(NotificationContext);
  const navigate = useNavigate();
  const { profile_id } = useParams();
  const isEdit = Boolean(profile_id);
  const { t } = useTranslation();

  document.title = pageTitle + ' - ' + (isEdit ? t('parents.edit_title') : t('parents.add_title'));

  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone_number: '', notes: '' });
  const [address, setAddress] = useState('');
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);
  const [markerPos, setMarkerPos] = useState(null);

  const [selectedChildren, setSelectedChildren] = useState([]);
  const [allChildren, setAllChildren] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingParent, setLoadingParent] = useState(isEdit);
  const [error, setError] = useState('');
  const [deletedProfileId, setDeletedProfileId] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const getChildren = useCallback(async () => {
    const response = await fetch(backendUrl + 'api/students/', {
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) },
    });
    if (response.ok) {
      const data = await response.json();
      setAllChildren(data.map((s) => ({ id: s.child_id, label: `${s.firstname} ${s.lastname}` })));
    }
  }, []);

  const loadParent = useCallback(async () => {
    const response = await fetch(backendUrl + 'api/parents/', {
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) },
    });
    if (!response.ok) { setLoadingParent(false); return; }
    const data = await response.json();
    const parent = data.find((p) => p.profile_id === profile_id);
    if (!parent) { setLoadingParent(false); return; }
    setForm({ first_name: parent.firstname || '', last_name: parent.lastname || '', email: parent.email || '', phone_number: parent.phone_number || '', notes: parent.notes || '' });
    setAddress(parent.address || '');
    if (parent.latitude && parent.longitude) {
      setLatitude(parseFloat(parent.latitude));
      setLongitude(parseFloat(parent.longitude));
      setMarkerPos([parseFloat(parent.latitude), parseFloat(parent.longitude)]);
    }
    if (parent.children) {
      setSelectedChildren(parent.children.map((c) => ({ id: c.child_id, label: `${c.firstname} ${c.lastname}` })));
    }
    setLoadingParent(false);
  }, [profile_id]);

  useEffect(() => { getChildren(); if (isEdit) loadParent(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'phone_number') {
      setForm((prev) => ({ ...prev, phone_number: value.replace(/\D/g, '').slice(0, 14) }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleMapClick = useCallback((lat, lng) => {
    setLatitude(lat); setLongitude(lng); setMarkerPos([lat, lng]);
  }, []);

  const handleSearchResult = useCallback(({ address: addr, lat, lng }) => {
    setAddress(addr); setLatitude(lat); setLongitude(lng); setMarkerPos([lat, lng]);
  }, []);

  const handleClearAddress = () => {
    setAddress(''); setLatitude(null); setLongitude(null); setMarkerPos(null);
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSave = async () => {
    if (!form.first_name.trim()) { setError(t('common.first_name_required')); return; }
    if (!form.last_name.trim()) { setError(t('common.last_name_required')); return; }
    if (!form.email) { setError(t('parents.email_required')); return; }
    if (!isValidEmail(form.email)) { setError(t('parents.email_invalid')); return; }
    if (form.phone_number.trim() && !/^\d{1,14}$/.test(form.phone_number)) { setError(t('common.phone_number_invalid')); return; }
    setSaving(true);
    setError('');
    setDeletedProfileId(null);
    const response = await fetch(backendUrl + 'api/save-parent/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) },
      body: JSON.stringify({ ...(isEdit ? { id: profile_id } : {}), ...form, address, latitude, longitude, child_ids: selectedChildren.map((c) => c.id) }),
    });
    setSaving(false);
    if (response.ok) {
      showToast(isEdit ? t('parents.updated') : t('parents.created'), 'success');
      navigate('/Parents');
    } else {
      let msg = t('parents.failed_to_save');
      let json = null;
      try { json = await response.json(); msg = json.error ? (t('parents.' + json.error, { defaultValue: '' }) || json.error) : msg; } catch { const text = await response.text(); msg = text || msg; }
      if (json && json.error === 'email_exists_deleted' && json.profile_id) {
        setDeletedProfileId(json.profile_id);
      }
      setError(msg);
    }
  };

  const handleRestore = async () => {
    if (!deletedProfileId) return;
    setRestoring(true);
    const response = await fetch(backendUrl + 'api/restore-parent/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) },
      body: JSON.stringify({ id: deletedProfileId }),
    });
    setRestoring(false);
    if (response.ok) {
      showToast(t('parents.restored', { name: `${form.first_name} ${form.last_name}` }), 'success');
      navigate(`/add-parent/${deletedProfileId}`);
    } else {
      showToast(t('common.network_error'), 'error');
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm(t('parents.confirm_delete', { name: `${form.first_name} ${form.last_name}` }))) return;
    setDeleting(true);
    const response = await fetch(backendUrl + 'api/delete-parent/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + String(authTokens.access) },
      body: JSON.stringify({ id: profile_id }),
    });
    setDeleting(false);
    if (response.ok) {
      showToast(t('parents.deleted', { name: `${form.first_name} ${form.last_name}` }), 'success');
      navigate('/Parents');
    } else {
      showToast(t('common.network_error'), 'error');
    }
  };

  const mapCenter = profile
    ? [parseFloat(profile.schoollatitude), parseFloat(profile.schoollongitude)]
    : [40, 22];

  if (loadingParent) return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <CircularProgress size={44} thickness={3} sx={{ color: AMBER }} />
    </Box>
  );

  return (
    <Container maxWidth="md" sx={{ mt: 12, mb: 6 }}>
      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', p: 4, boxShadow: '0 4px 24px rgba(15,45,94,0.07)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <PersonAddIcon sx={{ color: NAV_BG, fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 700, color: NAV_BG }}>
            {isEdit ? t('parents.edit_title') : t('parents.add_title')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label={t('parents.first_name')} name="first_name" value={form.first_name} onChange={handleChange} fullWidth size="small" required />
            <TextField label={t('parents.last_name')} name="last_name" value={form.last_name} onChange={handleChange} fullWidth size="small" required />
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label={t('parents.email')} name="email" type="email" value={form.email} onChange={handleChange} fullWidth size="small" required />
            <TextField label={t('parents.phone')} name="phone_number" value={form.phone_number} onChange={handleChange} fullWidth size="small" required inputProps={{ maxLength: 14 }} />
          </Box>
          <Autocomplete
            multiple
            options={allChildren}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={selectedChildren}
            onChange={(_, value) => setSelectedChildren(value)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip key={option.id} label={option.label} size="small"
                  sx={{ fontFamily: '"Outfit", sans-serif', fontSize: '0.78rem', background: 'rgba(15,45,94,0.08)', color: NAV_BG }}
                  {...getTagProps({ index })} />
              ))
            }
            renderInput={(params) => (
              <TextField {...params} label={t('parents.assign_children')} size="small" placeholder={t('parents.search_children')} />
            )}
          />
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Address */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <LocationOnIcon sx={{ color: NAV_BG, fontSize: 20 }} />
          <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, color: NAV_BG, fontSize: '0.95rem' }}>
            {t('parents.address')} <Typography component="span" sx={{ fontWeight: 400, color: '#94a3b8', fontSize: '0.85rem' }}>{t('parents.address_optional')}</Typography>
          </Typography>
        </Box>

        <Box sx={{ mb: 2 }}>
          <GoogleAddressSearch
            value={address}
            onChange={(val) => setAddress(val)}
            onSelect={handleSearchResult}
            label={t('parents.search_address')}
          />
        </Box>

        {isEdit && (address || markerPos) && (
          <Button size="small" onClick={handleClearAddress} startIcon={<DeleteOutlineIcon />}
            sx={{ mb: 2, fontFamily: '"Outfit", sans-serif', textTransform: 'none', color: '#dc2626' }}>
            {t('parents.remove_address')}
          </Button>
        )}

        <Paper elevation={0} sx={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', mb: 2 }}>
          <MapContainer center={mapCenter} zoom={14} style={{ height: 320, width: '100%', cursor: 'crosshair' }}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <ClickHandler onMapClick={handleMapClick} />
            <FlyTo position={markerPos} />
            {markerPos && <Marker position={markerPos} />}
          </MapContainer>
        </Paper>

        <Divider sx={{ my: 3 }} />

        <TextField
          label={t('parents.notes')}
          name="notes"
          value={form.notes}
          onChange={handleChange}
          fullWidth
          multiline
          minRows={3}
          size="small"
          placeholder={t('parents.notes_placeholder')}
        />

        <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ background: NAV_BG, fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { background: '#163571' } }}>
            {saving ? t('parents.saving') : t('parents.save')}
          </Button>
          <Button variant="outlined" onClick={() => navigate('/Parents')}
            sx={{ fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: AMBER, color: NAV_BG } }}>
            {t('parents.cancel')}
          </Button>
          {isEdit && (
            <Button variant="outlined" onClick={handleDelete} disabled={deleting}
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
              sx={{ ml: 'auto', fontFamily: '"Outfit", sans-serif', borderRadius: '8px', textTransform: 'none', borderColor: '#fecaca', color: '#b91c1c', '&:hover': { borderColor: '#b91c1c', background: 'rgba(185,28,28,0.06)' } }}>
              {deleting ? t('parents.deleting') : t('parents.delete_action')}
            </Button>
          )}
        </Box>
      </Paper>

      <Snackbar open={!!error} autoHideDuration={deletedProfileId ? null : 20000} onClose={() => { setError(''); setDeletedProfileId(null); }}>
        <Alert
          severity="error"
          onClose={() => { setError(''); setDeletedProfileId(null); }}
          action={deletedProfileId ? (
            <Button color="inherit" size="small" onClick={handleRestore} disabled={restoring}>
              {restoring ? t('parents.restoring') : t('parents.restore_action')}
            </Button>
          ) : undefined}
        >
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AddParentPage;
